import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { HIT_BOARD_STATUS } from 'redux/actions/board';
import { DO_LOADING } from 'redux/actions/loading';
import { apiBoard, boardService } from './board.service';

export const useBoardList = (memberId: null | string, enabled:boolean = true) => useQuery({
  queryKey: [apiBoard.boardList, memberId],
  queryFn: () => boardService.getBoardList({
    member_id: memberId,
  }),
  enabled: !!memberId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

const useMutationCategoryBoard = <
TPayload, TResponse extends { message: string },
>
  (mutationFn: (payload: TPayload) => Promise<TResponse>) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: () => { dispatch({ type: DO_LOADING, payload: true }); },
    onSuccess: (responseData: TResponse) => {
      const successMessage = responseData.message;

      void queryClient.invalidateQueries({
        queryKey: [apiBoard.boardList],
      });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'success',
          message: successMessage,
        },
      });
    },
    onError: (error) => {
      const errorMessage = error.message;
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          message: errorMessage,
        },
      });
    },
    onSettled: () => { dispatch({ type: DO_LOADING, payload: false }); },
  });
};

export const useCreateBoard = () => useMutationCategoryBoard(boardService.createBoard);

export const useUpdateBoard = () => useMutationCategoryBoard(boardService.updateBoard);

export const useDeleteBoard = () => useMutationCategoryBoard(boardService.deleteBoard);

export const useDuplicateBoard = () => useMutationCategoryBoard(boardService.duplicateBoard);


export const useMemberBoard = (boardId: null | string, enabled:boolean = true) => useQuery({
  queryKey: [apiBoard.boardMember, boardId],
  queryFn: () => boardService.getMemberBoard({
    board_id: boardId,
  }),
  enabled: !!boardId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
});

const useMutationCategoryMemberBoard = <
TPayload, TResponse extends { message: string },
>(
    mutationFn: (payload: TPayload) => Promise<TResponse>,
  ) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: () => { dispatch({ type: DO_LOADING, payload: true }); },
    onSuccess: (responseData : TResponse) => {
      const successMessage = responseData.message;

      void queryClient.invalidateQueries({
        queryKey: [apiBoard.boardMember],
      });
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'success',
          message: successMessage,
        },
      });
    },
    onError: (error) => {
      const errorMessage = error.message;
      dispatch({
        type: HIT_BOARD_STATUS,
        payload: {
          status: 'failed',
          message: errorMessage,
        },
      });
    },
    onSettled: () => { dispatch({ type: DO_LOADING, payload: false }); },
  });
};

export const useInviteMemberBoard = () => useMutationCategoryMemberBoard(boardService.inviteMemberBoard);

export const useDeleteMemberBoard = () => useMutationCategoryMemberBoard(boardService.deleteMemberBoard);
